<template>
  <div class="grants">
    <div class="grant-card">
      <div class="grant-info">
        <div class="main-info-top">
          <div class="title-badges">
            <div class="grant-text">
              <div class="title">
                <router-link
                  v-if="!isDummy"
                  style="text-decoration: none"
                  :to="{
                    name: 'GrantDetails',
                    params: { grantId: id },
                    query: { t: searchText },
                  }"
                  v-bind:target="openDetailsInNewTab ? '_blank' : null"
                  class="title-router-link"
                >
                  <h2 class="bold-font title" v-html="title"></h2>
                  <div class="badges">
                    <q-badge
                      class="badge"
                      v-if="isNew"
                      rounded
                      color="primary"
                      :label="t('search_schemes.new')"
                      :aria-label="t('search_schemes.new')"
                    />
                    <q-badge
                      class="badge"
                      v-if="isRecentlyChanged && !isNew"
                      rounded
                      color="primary"
                      :label="t('search_schemes.changed')"
                      :aria-label="t('search_schemes.changed')"
                    />
                  </div>
                </router-link>
                <span class="title-text" v-else v-html="title"></span>

                <GrantSelect
                  class="select-checkbox"
                  v-if="
                    route.name != 'NewestSchemes' &&
                    route.name != 'DeadlinesSchemes' &&
                    route.name != 'SignificantChanges'
                  "
                  :id="schemeId"
                  :allSelected="selectedResults"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="organisation-text">
              <span v-if="grant.adminsitratingOrganisations.length > 0">{{
                grant.adminsitratingOrganisations[0].value
              }}</span>
            </div>
            <q-space></q-space>
            <div
              class="fit justify-end q-mr-md q-mt-xl"
              style="max-width: 20%; display: none !important"
            >
              <q-slider
                v-if="isVind || isSKS"
                v-model="matchingPercentageValue"
                color="light-blue-2"
                :thumb-color="
                  matchingPercentageValue === 0 ? 'grey' : 'indigo-10'
                "
                thumb-size="15px"
                inner-track-color="transparent"
                :min="0"
                :max="107"
                :markers="21.4"
                :marker-labels="arrayMarkerLabel"
                switch-marker-labels
                readonly
                marker-labels-class="small-label"
                :label-value="t('search_schemes.match_filters')"
                label-always
                dense
              ></q-slider>
            </div>
          </div>
        </div>

        <div v-html="introduction" class="desc-short"></div>

        <div class="other-details">
          <dl class="text-details row">
            <dt class="detail-tag">{{ t("search_schemes.status") }}</dt>

            <dd v-if="!isDummy" class="detail-value bold-font">
              {{ grantStatus }}
            </dd>

            <dd v-else class="detail-value bold-font">
              {{ t("search_schemes.forthcoming") }}
            </dd>

            <dt class="detail-tag">{{ t("search_schemes.max_budget") }}</dt>
            <dd
              v-if="!grant.maxAmount || grant.maxAmount < 1 || isDummy"
              class="detail-value bold-font"
            >
              {{ t("search_schemes.indefinite") }}
            </dd>

            <dd
              v-if="grant.maxAmount && grant.maxAmount > 1"
              class="detail-value bold-font"
            >
              {{ formattedMaxAmount }}
            </dd>

            <dt class="detail-tag">
              {{ grant.currentDeadlineHeaderText }}
            </dt>
            <dd class="detail-value bold-font items-center">
              {{ grant.currentDeadlineText }}
              &nbsp;
              <i
                v-if="grant.currentDeadlineIcon"
                :class="`las la-1x ${grant.currentDeadlineIcon}`"
                :aria-label="grant.currentDeadlineIcon === 'la-infinity' ? t('scheme_details.always_open') : t('scheme_details.recurring_deadline')"
              >
                <q-tooltip v-if="grant.currentDeadlineIcon && grant.currentDeadlineIcon === 'la-infinity'">
                  {{ t("scheme_details.always_open") }}
                </q-tooltip>
                <q-tooltip v-if="grant.currentDeadlineIcon && grant.currentDeadlineIcon === 'la-redo-alt'">
                  {{ t("scheme_details.recurring_deadline") }}
                </q-tooltip>
              </i>
            </dd>
          </dl>
          <div class="action-icons" v-if="isVind || isSKS">
            <q-icon
              class="action-icon"
              name="fa-solid fa-floppy-disk"
              size="18px"
              @click="enableSaveScheme(schemeId)"
              id="v-step-7"
            >
              <q-tooltip> {{ t("search_schemes.save") }} </q-tooltip></q-icon
            >
            <q-icon
              class="action-icon"
              size="18px"
              name="fa-solid fa-eye-slash"
              @click="hideScheme(schemeId)"
              :aria-label="t('search_schemes.hide')"
              id="v-step-8"
            >
              <q-tooltip> {{ t("search_schemes.hide") }} </q-tooltip></q-icon
            >
            <q-icon
              v-if="!isSKS"
              class="action-icon"
              size="18px"
              name="fa-solid fa-share-nodes"
              @click="shareGrant()"
              :aria-label="t('search_schemes.share')"
              id="v-step-9"
            >
              <q-tooltip> {{ t("search_schemes.share") }} </q-tooltip></q-icon
            >
            <q-icon
              v-if="!isSKS"
              name="fa-solid fa-download"
              size="18px"
              class="action-icon q-ml-sm"
              @click="downloadSchemePopup(schemeId)"
              :aria-label="t('search_schemes.download')"
              id="v-step-10"
            >
              <q-tooltip>
                {{ t("search_schemes.download") }}
              </q-tooltip></q-icon
            >
            <q-icon
              v-if="grant.hasMultipleCurrentOpenings"
              name="warning"
              size="18px"
              class="action-icon q-ml-sm"
              :aria-label="t('search_schemes.multiple_openings')"
              id="v-step-10"
            >
              <q-tooltip>
                {{ t("search_schemes.multiple_openings") }}
              </q-tooltip>
            </q-icon>
          </div>
        </div>

        <div class="bottom-options" v-if="isVind">
          <div
            class="calls-toggle"
            v-if="!isFromHomepage && grant.calls.length > 0"
          >
            <q-btn
              v-if="!displayCalls"
              flat
              no-caps
              @click="toggleDisplayCalls()"
              :aria-label="t('search_schemes.show_calls')"
              class="calls-btn"
              >{{ t("search_schemes.show_calls") }} &nbsp;<i
                class="las la-arrow-down"
              ></i
            ></q-btn>

            <q-btn
              v-if="displayCalls"
              flat
              no-caps
              @click="toggleDisplayCalls()"
              :aria-label="t('search_schemes.hide_calls')"
              class="calls-btn"
              >{{ t("search_schemes.hide_calls") }} &nbsp;<i
                class="las la-arrow-up"
              ></i
            ></q-btn>
          </div>
        </div>
      </div>
    </div>
    <div class="grant-calls" v-if="displayCalls">
      <div class="grant-call calls-list">
        <SchemeCallCard
          v-for="call in grant.calls"
          :key="call.id"
          :grant="call"
          :id="call.id"
          :title="call.name"
          :introduction="call.introduction"
        />
        <div v-if="callCount > 10">
          <div class="row q-pt-sm q-pb-lg float-right">
            Nog {{ callCount - 10 }} openstellingen bekend voor deze
            regeling.&nbsp;&nbsp;&nbsp;
            <router-link
              style="text-decoration: none"
              :to="{ name: 'GrantDetails', params: { grantId: id } }"
              v-bind:target="openDetailsInNewTab ? '_blank' : null"
            >
              {{ t("search_schemes.open") }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import store from "../store/index";
import SchemeCallCard from "./SchemeCallCard.vue";
import GrantsService from "../services/grants.service";
import moment from "moment";
import SchemeUtils from "../utils/schemes";
import GrantSelect from "./GrantSelectButton.vue";
import UserService from "../services/userprofile.service";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

export default {
  name: "GrantCard",
  props: {
    title: String,
    introduction: String,
    currentDeadlineHeaderText: String,
    currentDeadlineText: String,
    organization: String,
    id: String,
    grant: Object,
    isDummy: Boolean,
    isFromHomepage: Boolean,
    matchingPercentage: Number,
  },
  components: {
    SchemeCallCard,
    GrantSelect,
  },
  setup(props) {
    const schemeId = ref(props.id);
    const { locale } = useI18n();

    onMounted(() => {
      checkIsNew();
      checkIsRecentlyChanged();
    });

    const grantStatus = ref(props.grant.status);
    const searchText = computed({
      get: () => store.state.advancedSearch.searchText,
    });

    const matchingPercentageValue = computed(() => {
      return props.matchingPercentage;
    });

    const isNew = ref(false);
    const checkIsNew = () => {
      var today = new Date();
      var newDateLimit = new Date().setDate(today.getDate() - 30);
      var created = moment(props.grant.createdDate).format("YYYY-MM-DD");
      var limit = moment(newDateLimit).format("YYYY-MM-DD");
      if (moment(created).isSameOrAfter(moment(limit))) isNew.value = true;
      else isNew.value = false;
    };

    const isRecentlyChanged = ref(false);
    const checkIsRecentlyChanged = () => {
      var today = new Date();
      var newDateLimit = new Date().setDate(today.getDate() - 30);
      var recent = moment(props.grant.updatedDate).format("YYYY-MM-DD");
      var limit = moment(newDateLimit).format("YYYY-MM-DD");
      if (moment(recent).isSameOrAfter(moment(limit)))
        isRecentlyChanged.value = true;
      else isRecentlyChanged.value = false;
    };

    const hasCalls = !props.isFromHomepage && props.grant.calls.length > 0;
    const callCount = !props.isFromHomepage && ref(props.grant.callCount);

    const toggleDisplayCalls = () => (displayCalls.value = !displayCalls.value);

    const displayCalls = ref(false);

    const shareGrant = () => {
      store.commit("grantItems/enableShareGrant", props.grant);
    };

    const shareGrantItem = computed({
      get: () => store.state.grantItems.shareGrantItem,
    });

    const schemeDetailsUrl = ref("/grant/" + props.id + "/details");
    const selectedResults = computed({
      get: () => store.state.grantItems.selectedResults,
    });

    const enableSaveScheme = (id) => {
      store.commit("grantItems/addSelected", id);
      UserService.getFolders();
      store.commit("user/enableSaveScheme");
    };

    const hideScheme = async (schemeId) => {
      await GrantsService.hideScheme(schemeId, locale);
      store.commit("grantItems/addSelected", schemeId);
      SchemeUtils.searchSchemes(locale);
    };

    const formattedMaxAmount = SchemeUtils.formatEuro(props.grant.maxAmount);

    const downloadSchemePopup = (id) => {
      store.commit("grantItems/enableDownloadSchemeReport", id);
    };

    const openDetailsInNewTab = computed({
      get: () => store.state.user.openDetailsInNewTab,
    });
    const route = useRoute();

    const isVind = computed({
      get: () => store.state.auth.env === 0 || store.state.auth.env === 40,
    });
    const isSKS = computed({
      get: () => store.state.auth.env === 30,
    });

    const arrayMarkerLabel = [
      { value: 0, label: "0%" },
      { value: 21.4, label: "20%" },
      { value: 42.8, label: "40%" },
      { value: 64.2, label: "60%" },
      { value: 85.6, label: "80%" },
      { value: 107, label: "100%" },
    ];
    return {
      locale,
      route,
      //downloadScheme,
      formattedMaxAmount,
      selectedResults,
      schemeDetailsUrl,
      shareGrantItem,
      shareGrant,
      isRecentlyChanged,
      checkIsRecentlyChanged,
      isNew,
      checkIsNew,
      downloadSchemePopup,
      toggleDisplayCalls,
      displayCalls,
      hasCalls,
      schemeId,
      enableSaveScheme,
      hideScheme,
      openDetailsInNewTab,
      callCount,
      searchText,
      isVind,
      isSKS,
      matchingPercentageValue,
      arrayMarkerLabel,
      grantStatus,
    };
  },
};
</script>
